import './instrument'; // Sentry must be first

import React from 'react';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { Toaster } from 'react-hot-toast';
import App from './App';
import './index.css';
// Self-hosted fonts (CSP is font-src 'self'; no third-party CDN fetches).
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './lib/theme'; // applies stored theme before first render to prevent flash
import { installPreloadErrorRecovery, registerServiceWorkerUpdates } from './lib/sw-update';
import { migrateOffLegacyOriginIfNeeded } from './lib/legacy-origin';

// An installed PWA on the retired *.fly.dev origin never sees nginx's 301
// (its service worker serves the shell from cache). Move it to the canonical
// origin before anything else — registering the worker or mounting the app
// here would only re-pin the broken origin.
if (migrateOffLegacyOriginIfNeeded()) {
  document.getElementById('root')!.textContent = 'Redirecting to ClinikChat…';
} else {
  installPreloadErrorRecovery();
  registerServiceWorkerUpdates();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
        {/* Mounted once at the root so toasts (including the service-worker
            "update available" prompt and session-degraded notices) render on
            every route, not only inside the signed-in AppLayout. */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#fafaf8',
              color: '#44403c',
              borderRadius: '12px',
              border: '1px solid #e5e2da',
              boxShadow: '0 4px 12px rgba(120, 100, 80, 0.1)',
              fontWeight: 500,
              fontSize: 'var(--text-body-sm)',
            },
            success: {
              iconTheme: {
                primary: '#2a9d6f',
                secondary: '#f5faf8',
              },
            },
            error: {
              iconTheme: {
                primary: '#d45d5d',
                secondary: '#fef0ef',
              },
            },
          }}
        />
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
}
